import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Clock, Video, MapPin } from "lucide-react";
import PageHeader from "../components/PageHeader";
import EntityForm from "../components/EntityForm";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { useEntity, createItem } from "../hooks/useEntity";
import { apiError } from "../lib/api";
import { formatDate } from "../lib/utils-format";
import { APPT_FIELDS } from "../config/recordConfigs";

export default function Calendar() {
  const navigate = useNavigate();
  const { items, total, loading, reload } = useEntity("appointments", { limit: 200, sort_by: "date", sort_dir: "asc" });
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (form) => {
    setSaving(true);
    try {
      await createItem("appointments", { ...form, status: form.status || "Scheduled" });
      toast.success("Appointment created");
      setFormOpen(false); reload();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const groups = {};
  items.forEach((a) => { const d = formatDate(a.date); (groups[d] = groups[d] || []).push(a); });

  return (
    <div className="p-6 lg:p-8 max-w-[1000px] mx-auto">
      <PageHeader title="Calendar" count={total} subtitle="Demos and appointments across your team"
        actions={<Button onClick={() => setFormOpen(true)} data-testid="new-record-btn"><Plus className="h-4 w-4 mr-1.5" />New Appointment</Button>} />

      {loading ? <p className="text-muted-foreground">Loading…</p> : Object.keys(groups).length === 0 ? (
        <div className="text-center py-16 text-muted-foreground border border-border rounded-lg">No appointments scheduled</div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groups).map(([date, appts]) => (
            <div key={date}>
              <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">{date}</div>
              <div className="space-y-2">
                {appts.map((a) => {
                  const Icon = a.meeting_method === "Video Call" ? Video : MapPin;
                  return (
                    <div key={a.id} onClick={() => navigate(`/record/appointments/${a.id}`)} data-testid={`appt-${a.id}`}
                      className="rounded-lg border border-border bg-card p-4 flex items-center gap-4 cursor-pointer hover:bg-accent/50 transition-colors">
                      <div className="flex flex-col items-center justify-center w-16 shrink-0 text-primary">
                        <Clock className="h-4 w-4" /><span className="text-xs mt-1">{a.start_time}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">{a.appointment_type} · {a.lead_name}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><Icon className="h-3 w-3" />{a.meeting_method}</div>
                      </div>
                      <StatusBadge value={a.status} />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <EntityForm open={formOpen} onOpenChange={setFormOpen} fields={APPT_FIELDS} initial={null} onSubmit={submit} saving={saving} title="New Appointment" />
    </div>
  );
}
